<script setup lang="ts">
import ToggleButton from 'primevue/togglebutton';
import { globalKeys } from 'assets/data/global-keys';
import Drawer from '../design-system/drawer.vue';
import Button from '~/components/design-system/button.vue';
import { actionHandlersConfig } from '~/utilities/query-actions';
import {
  AccountModalsStrings,
  siteModalsStrings,
} from '~/interfaces/dto/general/modals';

const { $storageService } = useServices();
const route = useRoute();
const site = useSiteStore();
const colorMode = useColorMode();

const queryArray = ref<string[]>([]);
const enabled = ref<boolean>(false);
const active = ref<boolean>(false);

const toggleDebug = (value: boolean) => {
  if (value) {
    enabled.value = true;
    $storageService.setSessionStorage({
      key: globalKeys.developerConfigKeys.debugQueryStrings,
      data: true,
    });
  } else {
    enabled.value = false;
    $storageService.removeSessionStorage({
      key: globalKeys.developerConfigKeys.debugQueryStrings,
    });
  }
};

const clearQueries = () => navigateTo(route.path);

const toggleRoute = (query: string) => {
  navigateTo(`${route.path === '/' ? '' : route.path}${query}`);
  active.value = false;
};

const testQueries = {
  ...actionHandlersConfig,
};

const siteMapArray = Array.from(site.sitemapMap).map((route) =>
  route[0].replace('/', ''),
);

// TODO: add more cases
const assignQueries = () =>
  (queryArray.value = Object.keys(testQueries).map((query) => {
    switch (query) {
      case 'modal':
        return `?modal=${siteModalsStrings[Math.floor(Math.random() * (siteModalsStrings.length - 1))]}`;
      case 'account':
        return `?account=${AccountModalsStrings[Math.floor(Math.random() * (AccountModalsStrings.length - 1))]}`;
      case 'optin':
        return `?optin=test`;
      case 'action':
        return `?action=navigateTo&actionValue=/home/featured/aviator`;
      case 'redirect':
        return `?redirect=/${siteMapArray[Math.floor(Math.random() * (siteMapArray.length - 1))]}`;
      case 'signupcode':
        return '?signupcode=test';
      case 'theme':
        return `?theme=${colorMode.value === 'dark' ? 'light' : 'dark'}`;
      case 'toast':
        return `?toast=/toast-messages/jackpotcity/jza/jza_beta_toast`;
      default:
        return `?${query}=true`;
    }
  }));

onMounted(() => {
  enabled.value = !!$storageService.getSessionStorage({
    key: globalKeys.developerConfigKeys.debugQueryStrings,
  });
  assignQueries();
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <Drawer
      v-model:visible="active"
      position="bottom"
      custom-container-class="bg-base-secondary p-4 overflow-auto border-none"
    >
      <template #activator>
        <p class="text-xs text-base-priority my-1">
          In order to see query string debug messages in the console, you need
          to toggle the button below and navigate with a query string.
        </p>
        <p class="text-xs text-base-priority my-1">
          you can also test queries from the drawer
        </p>
        <ToggleButton
          on-label="Query debug on"
          off-label="Query debug off"
          class="p-2 rounded-lg stacked-border text-sm truncate"
          :class="[
            {
              'bg-primary text-base-priority': !enabled,
              'bg-success-500 text-light-50 font-bold': enabled,
            },
          ]"
          :model-value="enabled"
          @change="toggleDebug(!enabled)"
        />
        <Button
          class="font-normal w-full justify-center"
          padding="md"
          rounding="md"
          @click="active = true"
        >
          Toggle query
        </Button>
      </template>
      <template #header>
        <div class="flex gap-3 mb-2" style="z-index: 100">
          <p class="text-base-priority text-lg font-bold">Queries</p>

          <Button
            type="secondary-alternative"
            padding="wide-short"
            rounding="md"
            @click="clearQueries"
          >
            Clear Queries
          </Button>
          <Button
            type="secondary-alternative"
            padding="wide-short"
            rounding="md"
            @click="assignQueries"
          >
            Randomize Queries
          </Button>
        </div>
      </template>
      <div class="text-base-priority max-h-[65vh] overflow-scroll">
        <div class="bg-layer p-2 rounded-lg col-span-3">
          <div class="font-bold">Test Queries</div>
          <hr class="mb-4 stacked-border-color" />
          <div class="grid grid-flow-col grid-rows-9 gap-2">
            <div
              v-for="query in queryArray"
              :key="'site-' + query"
              class="flex justify-between mb-1"
            >
              <Button
                class="font-normal std-border w-full"
                :type="route.fullPath.includes(query) ? 'primary' : 'tertiary'"
                padding="md"
                rounding="md"
                @click="toggleRoute(query)"
              >
                {{ query }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>
